// Filtros do Painel de Autos ↔ URL, e o link que leva à lista já filtrada
// (spec 2026-10-15 §3). A lista usa datas AAAA-MM-DD; o painel, meses — a
// conversão mora aqui, num lugar só.
import { FILTROS_VAZIOS, queryDosFiltros } from '../../DadosEstatisticos/ListarAutosInfracao/filtrosAutos';
import { formataFracao, formataNumero } from '../../../components/Painel/formata';

const NOMES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const LISTA = '/dados-estatisticos/autos-infracao';

// O que veio na URL vai como veio: valor inválido segue para o backend, que
// responde 400 dizendo o campo, e a tela mostra. Descartar em silêncio abria o
// período padrão sob um link que dizia outra coisa (#395).
export function filtrosDoPainel(search) {
  const p = new URLSearchParams(search || '');
  return { de: p.get('de') || '', ate: p.get('ate') || '', posto: p.get('posto') || '' };
}

// Depois de um 400, limpa só o campo que o backend apontou — senão o "Aplicar"
// reenviava o mesmo valor inválido.
export const limpaCampo = (rascunho, campo) =>
  (campo && Object.prototype.hasOwnProperty.call(rascunho, campo) ? { ...rascunho, [campo]: '' } : rascunho);

// Opções do select de posto. O posto escolhido entra mesmo fora da lista: depois
// de um 400 a lista pode estar vazia, e o select mostrava "Todos" enquanto o
// "Aplicar" seguinte filtrava pelo posto escondido (revisão do PR 4, M-3).
export const opcoesDePosto = (postos, selecionado) => {
  const lista = postos || [];
  return selecionado && !lista.includes(selecionado) ? [...lista, selecionado] : lista;
};

// Infração sem código na base da Receita (#395): nada de "null · …".
export const rotuloInfracao = ({ codigo, descricao }) =>
  `${codigo === null || codigo === undefined ? 'Sem código' : codigo} · ${descricao || '(sem descrição)'}`;

export function paramsDoPainel(f) {
  const r = {};
  ['de', 'ate', 'posto'].forEach((k) => { if (f[k]) r[k] = f[k]; });
  return r;
}

export function queryDoPainel(f) {
  const s = new URLSearchParams(paramsDoPainel(f)).toString();
  return s ? `?${s}` : '';
}

export const rotuloMes = (ym) => `${NOMES[Number(ym.slice(5, 7)) - 1]}/${ym.slice(2, 4)}`;
export const rotuloPeriodo = ({ de, ate }) => `${rotuloMes(de)}–${rotuloMes(ate)}`;

// Rótulo de cada coluna de "Mesma janela em cada ano". O backend identifica a
// janela pelo ano em que ela TERMINA: com jul–jun, "2026" era jul/25–jun/26 e
// lia-se como o ano civil. Janela que atravessa a virada mostra os dois anos.
export const rotuloJanela = (ano, { de, ate }) => (de.slice(0, 4) === ate.slice(0, 4)
  ? String(ano)
  : `${String(ano - 1).slice(2)}/${String(ano).slice(2)}`);

const ultimoDia = (ym) => {
  const [a, m] = ym.split('-').map(Number);
  return `${ym}-${String(new Date(Date.UTC(a, m, 0)).getUTCDate()).padStart(2, '0')}`;
};

export function linkLista(periodo, posto, extra = {}) {
  if (extra.status_acompanhamento === 'Sem acompanhamento') return null;
  // A lista filtra nu_infracao por igualdade num INTEGER: não há filtro de nulo.
  if ('nu_infracao' in extra && (extra.nu_infracao === null || extra.nu_infracao === undefined)) return null;
  const de = extra.mes || periodo.de;
  const ate = extra.mes || periodo.ate;
  const filtros = {
    ...FILTROS_VAZIOS,
    data_inicio: `${de}-01`,
    data_final: ultimoDia(ate),
    posto: extra.posto || posto || '',
    status_acompanhamento: extra.status_acompanhamento || '',
    nu_infracao: extra.nu_infracao !== undefined ? String(extra.nu_infracao) : '',
  };
  return `${LISTA}${queryDosFiltros(filtros)}`;
}

export const listaNomes = (nomes) =>
  (nomes.length <= 1 ? nomes.join('') : `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`);

// Postos que a base da Receita não atende pelo RF (conferido em 16/10/2026, no
// banco local e nos dados de produção da Ascom): último auto com RF 9914 em
// 2022 e com 9923 em 2014.
const SEM_RF_ATIVO = { 'Timbó': 2022, 'Angra dos Reis': 2014 };

// Nota da coluna "Autos / 100 abordagens" quando um posto aborda e não tem auto
// no período (revisão final do PR 3, I-1): a causa estrutural só para quem a
// tem; mês ainda não carregado é dito como tal — sem atribuir causa a todos.
export function notaSemAutos(postos, mesesIncompletos) {
  if (!postos.length) return null;
  const partes = [`Nenhum auto da Receita atribuído a ${listaNomes(postos)} no período.`];
  if (mesesIncompletos > 0) partes.push('O período inclui meses ainda não carregados da Receita.');
  const estruturais = postos.filter((p) => SEM_RF_ATIVO[p]);
  if (estruturais.length) {
    const desde = listaNomes(estruturais.map((p) => `de ${p} desde ${SEM_RF_ATIVO[p]}`));
    partes.push(`A base da Receita não traz autos com o RF ${desde}: `
      + 'os autos de lá, se houver, podem estar no RF de outro posto ou da Auditoria Especializada.');
  }
  return partes.join(' ');
}

// "Por posto" pelo valor lavrado (#398): na ordem fixa, a Auditoria — 26,7% do
// valor em out/26 — ficava abaixo de postos zerados. "Outros" é o resto, não um
// posto: fica por último. sort é estável, então empate mantém a ordem de origem.
export const ordenaPostos = (porPosto) => [...porPosto].sort((a, b) => (
  (a.posto === 'Outros') - (b.posto === 'Outros') || b.lavrado - a.lavrado
));

// Quanto as 10 maiores infrações concentram do valor (#398). Com 10 ou menos a
// lista já é tudo, e a frase seria "100%" sem informar nada.
export const notaConcentracao = ({ quantidade, participacao_top_valor: p } = {}) => (
  quantidade > 10 && p != null
    ? `As 10 maiores somam ${formataFracao(p)} do valor das ${formataNumero(quantidade)} infrações do período.`
    : null
);
